import { OVERLAY_FONT_FAMILIES } from '@/lib/fonts/load-google-font';

/**
 * Resolve which font a NEW text layer should be seeded with, from the active
 * brand kit's `font_primary`/`font_secondary`.
 *
 * `font_primary`/`font_secondary` are free-text columns on `brand_kits`
 * (scraped from a site or typed by a marketer), so either can name a family
 * the Media Studio editor cannot actually reproduce. `OVERLAY_FONT_FAMILIES`
 * is the ONLY set the client (`loadGoogleFont`) and the server compositor
 * (`ALLOWED_FAMILIES` in `server/api/_shared/overlay/font-provider.ts`) both
 * honour end to end — everything else is refused server-side at export time.
 * Seeding a family outside that set would look fine on canvas (the browser
 * loads real Google Font names on faith) and then throw or silently diverge
 * on export, which is worse than doing nothing.
 *
 * So the chain is honest, not adventurous: `font_primary` if it's in the
 * allowed set, else `font_secondary` if it's in the allowed set, else the
 * plain 'Inter' default — never a guess at a nearby-sounding family.
 */
export function resolveBrandFontFamily(
  fontPrimary: string | null | undefined,
  fontSecondary: string | null | undefined,
): string {
  const allowed = new Set<string>(OVERLAY_FONT_FAMILIES);
  const primary = fontPrimary?.trim();
  if (primary && allowed.has(primary)) return primary;
  const secondary = fontSecondary?.trim();
  if (secondary && allowed.has(secondary)) return secondary;
  return 'Inter';
}
